import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeHelpCenter, updateHelpCenter } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  helpCenterId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  title: "",
  link: "",
  status: "",
};

const HelpCenterFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  helpCenterId,
}) => {
  const isAddMode = !helpCenterId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const [status, setStatus] = useState<ValueSchema | null>(null);

  const [statusItems] = React.useState([
    { label: "Active", value: 1 },
    { label: "Non Active", value: 0 },
  ]);

  const { helpCenters, isFormLoading } = useSelector(
    (state: any) => state.helpCenterReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setStatus(null);
  };

  const handleSubmit = (values: any) => {
    const { title, link, status } = values;

    isAddMode
      ? dispatch(
          storeHelpCenter({ title, link, status }, handleCloseModal) as any
        )
      : dispatch(
          updateHelpCenter(
            helpCenterId,
            { title, link, status },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const helpCenterDetail = helpCenters.find(
        (helpCenter: any) => helpCenter.id === parseInt(helpCenterId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("title", helpCenterDetail.title);
        formikRef.current.setFieldValue("link", helpCenterDetail.link);
        formikRef.current.setFieldValue("status", helpCenterDetail.is_active);

        statusItems.map((item) =>
          item.value === helpCenterDetail.is_active ? setStatus(item) : null
        );
      }
    }
  }, [isAddMode, helpCenters, helpCenterId]);

  const validationSchema = yup.object({
    title: yup.string().required("Title is required field"),
    link: yup.string().required("Link is required field"),
    status: yup.string().required("Status is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Help Center" : "Update Help Center"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="title">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="title">Title</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="link">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="link">Link</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="status">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="status">Status</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={status}
                            width={"100%"}
                            placeholder="Select Status"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setStatus(selectedItem);
                                setFieldValue("status", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={statusItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default HelpCenterFormModal;
